import EmblaCarousel from 'embla-carousel';
import { gsap } from './animations';

/**
 * Lightbox custom riutilizzabile.
 *
 * Trigger: elementi [data-tm-lightbox="<gruppo>"] con data-full (+ data-caption).
 * Gli elementi con lo stesso gruppo formano un set navigabile. La shell è
 * singleton ([data-tm-lightbox-root]), stampata in wp_footer.
 *
 * A11y: dialog con aria-modal, focus al pannello e ripristino al trigger,
 * sfondo reso inert, Esc per chiudere. Swipe via Embla, frecce, click metà
 * schermo. Cursore custom solo come enhancement su device con hover.
 */
export function initLightbox() {
	const lb       = document.querySelector( '[data-tm-lightbox-root]' );
	const triggers = [ ...document.querySelectorAll( '[data-tm-lightbox]' ) ];
	if ( ! lb || ! triggers.length ) {
		return;
	}

	const panel    = lb.querySelector( '[data-lightbox-panel]' );
	const viewport = lb.querySelector( '[data-lightbox-viewport]' );
	const track    = lb.querySelector( '[data-lightbox-track]' );
	const overlay  = lb.querySelector( '[data-lightbox-overlay]' );
	const btnClose = lb.querySelector( '[data-lightbox-close]' );
	const btnPrev  = lb.querySelector( '[data-lightbox-prev]' );
	const btnNext  = lb.querySelector( '[data-lightbox-next]' );
	const elCurr   = lb.querySelector( '[data-lightbox-curr]' );
	const elTotal  = lb.querySelector( '[data-lightbox-total]' );
	const elCap    = lb.querySelector( '[data-lightbox-caption]' );
	const live     = lb.querySelector( '[data-lightbox-live]' );
	const cta      = lb.querySelector( '[data-lightbox-cta]' );

	const reduce    = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
	const INERT_IDS = [ 'wpadminbar', 'site-header', 'main', 'contatti' ];
	const i18n      = window.tm13mI18n || {};

	let embla        = null;
	let lastFocused  = null;
	let currentItems = [];

	// Raggruppa i trigger per valore di data-tm-lightbox.
	const groups = new Map();
	triggers.forEach( ( t ) => {
		const key = t.dataset.tmLightbox || 'default';
		if ( ! groups.has( key ) ) {
			groups.set( key, [] );
		}
		groups.get( key ).push( t );
	} );

	function buildSlides( items ) {
		track.replaceChildren(
			...items.map( ( it ) => {
				const slide = document.createElement( 'div' );
				slide.className = 'tm-lightbox__slide';
				const img = document.createElement( 'img' );
				img.src = it.dataset.full || it.getAttribute( 'href' ) || '';
				img.alt = it.dataset.caption || '';
				img.draggable = false;
				slide.appendChild( img );
				return slide;
			} )
		);
	}

	function updateCounter() {
		if ( ! embla ) {
			return;
		}
		const n = embla.selectedScrollSnap() + 1;
		elCurr.textContent = n;
		// Didascalia visibile dell'immagine corrente.
		if ( elCap ) {
			const text = currentItems[ n - 1 ]?.dataset.caption || '';
			elCap.textContent = text;
			elCap.classList.toggle( 'is-empty', '' === text );
		}
		// Annuncio per screen reader (il contatore visivo è aria-hidden).
		if ( live ) {
			const tpl = i18n.imageCounter || 'Immagine %1$s di %2$s';
			live.textContent = tpl.replace( '%1$s', n ).replace( '%2$s', elTotal.textContent );
		}
	}

	function setInert( on ) {
		INERT_IDS.forEach( ( id ) => {
			const el = document.getElementById( id );
			if ( el ) {
				el.inert = on;
			}
		} );
	}

	function open( key, trigger ) {
		// Solo i trigger visibili: la stagionalità può nascondere celle (display:none).
		const items = ( groups.get( key ) || [] ).filter( ( el ) => null !== el.offsetParent );
		if ( ! items.length ) {
			return;
		}
		const index = Math.max( 0, items.indexOf( trigger ) );
		currentItems = items;
		lastFocused = document.activeElement;

		buildSlides( items );
		elTotal.textContent = items.length;

		lb.hidden = false;
		void lb.offsetWidth; // reflow: abilita la transizione di apertura

		embla = EmblaCarousel( viewport, { loop: true, startIndex: index, align: 'center' } );
		embla.on( 'select', updateCounter );
		updateCounter();

		lb.classList.add( 'is-open' );
		lb.removeAttribute( 'aria-hidden' );
		document.body.classList.add( 'tm-no-scroll' );
		setInert( true );
		panel.focus();
	}

	function close() {
		if ( ! lb.classList.contains( 'is-open' ) ) {
			return;
		}
		lb.classList.remove( 'is-open' );
		document.body.classList.remove( 'tm-no-scroll' );
		setInert( false );
		setCursorMode( null );

		// Il focus va spostato FUORI dal lightbox prima di aria-hidden: gli AT
		// bloccano aria-hidden su un antenato dell'elemento con focus (WAI-ARIA).
		// Inert va già tolto sopra, così il trigger può riprendere il focus.
		if ( lastFocused && lastFocused.focus ) {
			lastFocused.focus();
		} else if ( document.activeElement && lb.contains( document.activeElement ) ) {
			document.activeElement.blur();
		}
		lb.setAttribute( 'aria-hidden', 'true' );

		const done = () => {
			lb.hidden = true;
			if ( embla ) {
				embla.destroy();
				embla = null;
			}
			track.replaceChildren();
		};

		if ( reduce ) {
			done();
			return;
		}
		// Fallback: se la transizione non scatta (tab in background, ecc.)
		// la pulizia avviene comunque.
		const timer = setTimeout( finish, 400 );
		function finish() {
			clearTimeout( timer );
			lb.removeEventListener( 'transitionend', onEnd );
			done();
		}
		const onEnd = ( e ) => {
			if ( e.target !== lb || e.propertyName !== 'opacity' ) {
				return;
			}
			finish();
		};
		lb.addEventListener( 'transitionend', onEnd );
	}

	// — Eventi —
	groups.forEach( ( items, key ) => {
		items.forEach( ( t ) => {
			t.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				open( key, t );
			} );
		} );
	} );

	btnClose.addEventListener( 'click', close );
	overlay.addEventListener( 'click', close );
	btnPrev.addEventListener( 'click', () => embla && embla.scrollPrev() );
	btnNext.addEventListener( 'click', () => embla && embla.scrollNext() );
	if ( cta ) {
		cta.addEventListener( 'click', close );
	}

	// Click sulla metà sx/dx dell'immagine → prev/next (ignora i drag:
	// Embla 8 non espone più clickAllowed(), rilevo lo spostamento a mano).
	let downX = 0;
	let downY = 0;
	viewport.addEventListener( 'pointerdown', ( e ) => {
		downX = e.clientX;
		downY = e.clientY;
	} );
	viewport.addEventListener( 'click', ( e ) => {
		if ( ! embla ) {
			return;
		}
		if ( Math.hypot( e.clientX - downX, e.clientY - downY ) > 8 ) {
			return;
		}
		if ( e.clientX < window.innerWidth / 2 ) {
			embla.scrollPrev();
		} else {
			embla.scrollNext();
		}
	} );

	document.addEventListener( 'keydown', ( e ) => {
		if ( ! lb.classList.contains( 'is-open' ) ) {
			return;
		}
		if ( 'Escape' === e.key ) {
			close();
		} else if ( 'ArrowRight' === e.key ) {
			embla && embla.scrollNext();
		} else if ( 'ArrowLeft' === e.key ) {
			embla && embla.scrollPrev();
		}
	} );

	// — Cursore custom (enhancement) —
	let cursor = null;
	let cursorArrow = null;
	let cursorText = null;

	function setCursorMode( mode ) {
		if ( ! cursor ) {
			return;
		}
		cursor.classList.toggle( 'is-active', !! mode );
		cursor.classList.toggle( 'is-nav', 'prev' === mode || 'next' === mode );
		if ( mode ) {
			const arrows = { scopri: '↗', prev: '←', next: '→' };
			cursorArrow.textContent = arrows[ mode ];
		}
	}

	function initCursor() {
		const canHover = window.matchMedia( '(hover: hover) and (pointer: fine)' ).matches;
		// Niente cursore custom (movimento con lag) se l'utente riduce il movimento.
		if ( ! canHover || reduce ) {
			return;
		}

		cursor = document.createElement( 'div' );
		cursor.className = 'tm-cursor';
		cursor.setAttribute( 'aria-hidden', 'true' );
		// Markup statico (nessun input utente); il testo localizzato lo impostiamo
		// dopo via textContent.
		cursor.innerHTML =
			'<div class="tm-cursor__disk"></div>' +
			'<svg class="tm-cursor__svg" viewBox="0 0 100 100"><defs>' +
			'<path id="tmCursorRing" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0"/></defs>' +
			'<text><textPath href="#tmCursorRing" startOffset="0"></textPath></text></svg>' +
			'<span class="tm-cursor__arrow">↗</span>';
		document.body.appendChild( cursor );
		document.documentElement.classList.add( 'tm-has-cursor' );
		cursorArrow = cursor.querySelector( '.tm-cursor__arrow' );
		cursorText = cursor.querySelector( 'textPath' );
		cursorText.textContent = ( ( i18n.discover || 'Scopri' ) + ' • ' ).repeat( 3 );

		gsap.set( cursor, { xPercent: -50, yPercent: -50 } );
		const xTo = gsap.quickTo( cursor, 'x', { duration: 0.28, ease: 'power3.out' } );
		const yTo = gsap.quickTo( cursor, 'y', { duration: 0.28, ease: 'power3.out' } );
		window.addEventListener( 'mousemove', ( e ) => {
			xTo( e.clientX );
			yTo( e.clientY );
		} );

		const isOpen = () => lb.classList.contains( 'is-open' );

		triggers.forEach( ( t ) => {
			t.addEventListener( 'mouseenter', () => {
				if ( ! isOpen() ) {
					setCursorMode( 'scopri' );
				}
			} );
			t.addEventListener( 'mouseleave', () => {
				if ( ! isOpen() ) {
					setCursorMode( null );
				}
			} );
		} );

		viewport.addEventListener( 'mousemove', ( e ) => {
			if ( isOpen() ) {
				setCursorMode( e.clientX < window.innerWidth / 2 ? 'prev' : 'next' );
			}
		} );
		viewport.addEventListener( 'mouseleave', () => {
			if ( isOpen() ) {
				setCursorMode( null );
			}
		} );
		[ btnClose, btnPrev, btnNext, cta ].forEach( ( el ) => {
			if ( el ) {
				el.addEventListener( 'mouseenter', () => setCursorMode( null ) );
			}
		} );
	}

	initCursor();
}
